import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import {
  CurrencyRupeeIcon,
  ClockIcon,
  ChartBarIcon,
  CalculatorIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
  CheckCircleIcon,
  SparklesIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

import { formatCurrency, debounce } from '../../utils/helpers';
import SEO from '../SEO/SEO';

// -------------------------------------------------------------
// 1. Static Hero Banner Section
// -------------------------------------------------------------
const StaticHeroSection = () => {
  const { t, i18n } = useTranslation();
  const isTamil = i18n.language === 'ta';

  const banner = {
    title: isTamil ? "அஹம் வீட்டுக் கடன் இஎம்ஐ கால்குலேட்டர்" : "Plan Your Home Loan with Aham EMI Calculator",
    subtitle: isTamil
      ? "உங்கள் மாதாந்திர தவணைகள், செலுத்த வேண்டிய மொத்த வட்டி ஆகியவற்றைக் கணக்கிட்டு, உங்கள் வீட்டுக் கடன் பயணத்தை எளிதாகத் திட்டமிடுங்கள்."
      : "Calculate your monthly installments, total interest, and design a comfortable repayment plan for your dream home.",
    backgroundImage: "/img/Home_banner/EMI_banner.png",
    primary_button_text: isTamil ? "இப்போது கணக்கிடுங்கள்" : "Calculate Now",
    primary_button_link: "#emi-calculator"
  };

  const handleScrollToCalculator = (e) => {
    e.preventDefault();
    const element = document.getElementById('emi-calculator');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative h-screen max-h-[600px] min-h-[50vh] overflow-hidden bg-gray-900">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${banner.backgroundImage})`,
        }}
        role="img"
        aria-label="Aham Housing Finance EMI Calculator Banner"
      >
        <img
          src={banner.backgroundImage}
          alt="Aham Housing Finance EMI Calculator Banner"
          className="hidden"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 h-full flex items-center justify-start">
        <div className="max-w-4xl w-full text-left">
          <div className="space-y-6 flex flex-col items-start">
            {/* Breadcrumbs */}
            <div className="flex items-center space-x-2 text-white/80 text-sm font-medium">
              <Link to="/" className="hover:text-white transition-colors">
                {isTamil ? "முகப்பு" : "Home"}
              </Link>
              <span>/</span>
              <span className="text-yellow-300 font-semibold">
                {isTamil ? "இஎம்ஐ கால்குலேட்டர்" : "EMI Calculator"}
              </span>
            </div>

            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-3xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-white"
            >
              {banner.title}
            </motion.h1>

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-lg md:text-xl text-white/90 max-w-2xl font-medium leading-relaxed"
            >
              {banner.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col items-start sm:flex-row gap-4 pt-4 w-full sm:w-auto"
            >
              <a
                href={banner.primary_button_link}
                onClick={handleScrollToCalculator}
                className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-3 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto sm:mx-0 animate-bounce"
              >
                <span>{banner.primary_button_text}</span>
                <ArrowRightIcon className="h-4 w-4" />
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 2. Static EMI Calculator Section
// -------------------------------------------------------------
const StaticEMICalculatorSection = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    loanAmount: 5000000,
    interestRate: 8.5,
    tenureYears: 20,
  });

  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const localCalculateEMI = (loanAmount, interestRate, tenureMonths) => {
    const r = interestRate / (12 * 100);
    const n = tenureMonths;
    const emi = loanAmount * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    const totalAmount = emi * n;
    const totalInterest = totalAmount - loanAmount;

    return {
      monthly_emi: emi,
      total_amount: totalAmount,
      total_interest: totalInterest,
    };
  };

  const calculateEMI = useCallback(
    debounce(async (amount, rate, years) => {
      if (amount < 100000 || rate < 5 || years < 1) return;

      setIsCalculating(true);
      try {
        const data = localCalculateEMI(amount, rate, years * 12);
        setResult(data);
      } catch (error) {
        console.error('EMI calculation failed:', error);
      } finally {
        setIsCalculating(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    calculateEMI(formData.loanAmount, formData.interestRate, formData.tenureYears);
  }, [formData, calculateEMI]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const isTamil = i18n.language === 'ta';

  const settings = {
    section_title: "Estimate Your Monthly Home Loan EMI",
    section_subtitle: "Planning to buy, build, improve or extend your home? Use our free EMI calculator to estimate your monthly home loan repayment before you apply.",
    cta_text: "Apply Now",
    cta_link: "/contact"
  };

  const cards = [
    { id: 'default1', card_key: 'principal', title: 'Principal', icon_name: 'CurrencyRupeeIcon', color_class: 'blue' },
    { id: 'default2', card_key: 'total_interest', title: 'Total Interest', icon_name: 'ClockIcon', color_class: 'orange' },
    { id: 'default3', card_key: 'total_amount', title: 'Total Amount Payable', icon_name: 'ChartBarIcon', color_class: 'green' }
  ];

  return (
    <section id="emi-calculator" className="py-20 bg-gradient-light relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 80%, #283079 0%, transparent 50%),
                           radial-gradient(circle at 80% 20%, #283079 0%, transparent 50%),
                           radial-gradient(circle at 40% 40%, #4f46e5 0%, transparent 50%)`,
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={containerVariants}
          className="text-center mb-12"
        >
          <motion.div variants={itemVariants} className="flex justify-center mb-6">
            <div className="bg-primary-100 p-4 rounded-2xl">
              <CalculatorIcon className="h-12 w-12 text-primary-600" />
            </div>
          </motion.div>

          <motion.h2
            variants={itemVariants}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            {isTamil ? t('emiCalculator.title') : settings.section_title}
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-2xl mx-auto"
          >
            {isTamil ? t('emiCalculator.subtitle') : settings.section_subtitle}
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto"
        >
          <motion.div variants={itemVariants} className="bg-white rounded-3xl shadow-large p-8 lg:p-12">
            <div className="space-y-8">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t('emiCalculator.loanAmount')}
                </label>
                <div className="space-y-4">
                  <input
                    type="range"
                    min="100000"
                    max="50000000"
                    step="100000"
                    value={formData.loanAmount}
                    onChange={(e) => handleInputChange('loanAmount', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    {isTamil ? (
                      <>
                        <span>₹1 லட்சம்</span>
                        <span className="font-semibold text-primary-600 text-lg">
                          {formatCurrency(formData.loanAmount)}
                        </span>
                        <span>₹5 கோடி</span>
                      </>
                    ) : (
                      <>
                        <span>₹1L</span>
                        <span className="font-semibold text-primary-600 text-lg">
                          {formatCurrency(formData.loanAmount)}
                        </span>
                        <span>₹5Cr</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t('emiCalculator.interestRate')}
                </label>
                <div className="space-y-4">
                  <input
                    type="range"
                    min="5"
                    max="20"
                    step="0.1"
                    value={formData.interestRate}
                    onChange={(e) => handleInputChange('interestRate', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>5%</span>
                    <span className="font-semibold text-primary-600 text-lg">
                      {formData.interestRate}% {isTamil ? 'வருடத்திற்கு' : 'p.a.'}
                    </span>
                    <span>20%</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t('emiCalculator.tenure')}
                </label>
                <div className="space-y-4">
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="1"
                    value={formData.tenureYears}
                    onChange={(e) => handleInputChange('tenureYears', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>1 {isTamil ? 'ஆண்டு' : 'Year'}</span>
                    <span className="font-semibold text-primary-600 text-lg">
                      {formData.tenureYears} {isTamil ? 'ஆண்டுகள்' : 'Years'}
                    </span>
                    <span>30 {isTamil ? 'ஆண்டுகள்' : 'Years'}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-8">
            {result && !isCalculating ? (
              <>
                <div className="flex flex-col xl:flex-row gap-6">
                  <div className="flex-1 bg-white rounded-3xl shadow-soft border border-gray-100 p-8 relative overflow-hidden flex flex-col items-center justify-center min-h-[320px] text-center">
                    <div className="relative z-10 flex flex-col items-center">
                      <div className="flex flex-col items-center mb-4">
                        <CurrencyRupeeIcon className="h-10 w-10 text-primary-600 mb-2" />
                        <span className="text-gray-600 text-lg uppercase tracking-wider font-semibold">
                          {t('emiCalculator.monthlyEmi')}
                        </span>
                      </div>
                      <div className="text-4xl lg:text-5xl font-extrabold mb-2 text-gray-900">
                        {formatCurrency(result.monthly_emi)}
                      </div>
                      <div className="text-gray-500 text-sm font-medium">
                        {isTamil ? t('emiCalculator.monthlyPaymentDetail') : 'Monthly Payment Detail'}
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 bg-white rounded-3xl shadow-soft p-6 flex flex-col items-center justify-center min-h-[320px]">
                    <h3 className="text-sm font-bold text-gray-500 mb-4 text-center uppercase tracking-wider">
                      {isTamil ? t('emiCalculator.breakupTitle') : 'Break-up of Total Payment'}
                    </h3>
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: isTamil ? t('emiCalculator.principalLabel') : 'Principal Loan Amount', value: formData.loanAmount },
                              { name: isTamil ? t('emiCalculator.interestLabel') : 'Total Interest', value: result.total_interest }
                            ]}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                              const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                              const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
                              const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
                              return (
                                <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="text-xs font-bold">
                                  {`${(percent * 100).toFixed(0)}%`}
                                </text>
                              );
                            }}
                            labelLine={false}
                          >
                            <Cell fill="#283079" />
                            <Cell fill="#ed2636" />
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend verticalAlign="bottom" height={36} iconType="square" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {cards.map((card) => {
                    const IconComponent = {
                      CurrencyRupeeIcon,
                      ClockIcon,
                      ChartBarIcon
                    }[card.icon_name] || CurrencyRupeeIcon;

                    const colorMap = {
                      blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
                      orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
                      green: { bg: 'bg-green-100', text: 'text-green-600' }
                    };

                    const colors = colorMap[card.color_class] || colorMap.blue;

                    const valueMap = {
                      principal: formData.loanAmount,
                      total_interest: result.total_interest,
                      total_amount: result.total_amount
                    };

                    const localizedTitleMap = {
                      principal: t('emiCalculator.principalLabel'),
                      total_interest: t('emiCalculator.interestLabel'),
                      total_amount: t('emiCalculator.totalAmount')
                    };

                    const displayTitle = isTamil ? localizedTitleMap[card.card_key] : card.title;

                    return (
                      <div key={card.id} className="bg-white rounded-2xl shadow-soft p-6 flex flex-col items-center text-center">
                        <div className="flex flex-col items-center mb-3">
                          <div className={`${colors.bg} p-3 rounded-xl mb-2`}>
                            <IconComponent className={`h-6 w-6 ${colors.text}`} />
                          </div>
                          <span className="text-gray-600 font-semibold text-sm uppercase tracking-wider">
                            {displayTitle}
                          </span>
                        </div>
                        <div className="text-2xl font-extrabold text-gray-900">
                          {formatCurrency(valueMap[card.card_key] || 0)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-center mt-6">
                  <Link
                    to={settings.cta_link}
                    className="bg-[#ed2636] text-white px-6 py-3 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto hover:bg-red-700"
                  >
                    <span>{isTamil ? t('common.applyNow') : settings.cta_text}</span>
                  </Link>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-3xl shadow-large p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-600">{isTamil ? 'கணக்கிடுகிறது...' : 'Calculating EMI...'}</p>
              </div>
            )}
          </motion.div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="max-w-7xl mx-auto mt-8 bg-blue-50 rounded-2xl p-6 border border-blue-100 flex flex-col sm:flex-row items-start gap-4 shadow-sm"
        >
          <InformationCircleIcon className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-relaxed font-medium">
            <span className="font-bold">{isTamil ? 'முக்கியமான குறிப்பு:' : 'Important:'}</span> {isTamil ? 'இந்த கால்குலேட்டர் ஒரு தோராயமான மதிப்பீட்டை மட்டுமே வழங்குகிறது. உங்களின் இறுதி EMI, கடன் தொகை, வட்டி விகிதம் மற்றும் பதவிக்காலம் ஆகியவை உங்களின் சுயவிவரம், திருப்பிச் செலுத்தும் திறன், சொத்து விவரங்கள், ஆவணங்கள் மற்றும் அஹம் HFC இன் இறுதி மதிப்பீடு ஆகியவற்றைப் பொறுத்தது.' : 'This calculator provides an estimate only. Your final EMI, loan amount, interest rate and tenure will depend on your profile, repayment capacity, property details, documentation and Aham HFC’s final assessment.'}
          </p>
        </motion.div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 24px;
          width: 24px;
          border-radius: 50%;
          background: #283079;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(40, 48, 121, 0.4);
          border: 3px solid white;
        }

        .slider::-moz-range-thumb {
          height: 24px;
          width: 24px;
          border-radius: 50%;
          background: #283079;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 4px 12px rgba(40, 48, 121, 0.4);
        }
      `}</style>
    </section>
  );
};

// -------------------------------------------------------------
// 3. Static Loan Guide Section
// -------------------------------------------------------------
const StaticLoanGuideSection = () => {
  const { t, i18n } = useTranslation();
  const isTamil = i18n.language === 'ta';

  const loanData = [
    { type: 'Home Loan Purchase — New/Existing', amount: 'Rs. 5 lakh to Rs. 50 lakh', tenure: '20 years', ltv: 'Up to 75%' },
    { type: 'Home Loan Self-Construction', amount: 'Rs. 5 lakh to Rs. 30 lakh', tenure: '20 years', ltv: 'Up to 70%' },
    { type: 'Composite Loan — Plot + Construction', amount: 'Rs. 5 lakh to Rs. 25 lakh', tenure: '20 years', ltv: 'Up to 60%' },
    { type: 'Home Improvement/Renovation/Repair', amount: 'Rs. 5 lakh to Rs. 15 lakh', tenure: '10 years', ltv: 'Up to 60%' },
    { type: 'Home Extension', amount: 'Rs. 5 lakh to Rs. 15 lakh', tenure: '10 years', ltv: 'Up to 60%' },
    { type: 'Home Enhancement', amount: 'Rs. 5 lakh to Rs. 15 lakh', tenure: '10 years', ltv: 'Up to 60%' },
    { type: 'Home Loan Balance Transfer', amount: 'Rs. 5 lakh to Rs. 25 lakh', tenure: '20 years', ltv: 'Up to 60%' },
    { type: 'NRI Home Loan', amount: 'Rs. 10 lakh to Rs. 50 lakh', tenure: '15 years', ltv: 'Up to 70%' },
    { type: 'Loan Against Property', amount: 'Rs. 5 lakh to Rs. 50 lakh', tenure: '10 years', ltv: 'Up to 50%' },
  ];

  return (
    <section className="py-20 bg-slate-50 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary-100 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-100 rounded-full blur-3xl opacity-50 translate-y-1/2 -translate-x-1/2"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center justify-center p-3 bg-primary-50 rounded-2xl mb-6 shadow-sm"
          >
            <DocumentTextIcon className="w-8 h-8 text-primary-600" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-5xl font-extrabold text-slate-800 mb-6 tracking-tight"
          >
            {isTamil ? 'கடன் தொகை மற்றும் கால வழிகாட்டி' : 'Loan Amount and Tenure Guide'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-600 font-medium leading-relaxed"
          >
            {isTamil
              ? 'இறுதித் தொகை மற்றும் பதவிக்காலம் தயாரிப்பு, விண்ணப்பதாரரின் சுயவிவரம், சொத்து மதிப்பீடு மற்றும் கொள்கை விதிமுறைகளைப் பொறுத்தது. கீழேயுள்ள அட்டவணை அஹம் HFC இன் தயாரிப்பு கட்டமைப்பிற்கான பரந்த வழிகாட்டியாகும்.'
              : 'The final amount and tenure depend on the product, applicant profile, property assessment and policy norms. The table below is only a broad guide to Aham HFC’s product structure.'}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="max-w-6xl mx-auto"
        >
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-primary-900 text-white">
                    <th className="py-5 px-6 font-semibold text-sm uppercase tracking-wider border-b border-primary-800 whitespace-nowrap">{isTamil ? 'கடன் வகை' : 'Loan Type'}</th>
                    <th className="py-5 px-6 font-semibold text-sm uppercase tracking-wider border-b border-primary-800 whitespace-nowrap">{isTamil ? 'கடன் தொகை வரம்பு' : 'Loan Amount Range'}</th>
                    <th className="py-5 px-6 font-semibold text-sm uppercase tracking-wider border-b border-primary-800 whitespace-nowrap">{isTamil ? 'அதிகபட்ச பதவிக்காலம்' : 'Maximum Tenure'}</th>
                    <th className="py-5 px-6 font-semibold text-sm uppercase tracking-wider border-b border-primary-800 whitespace-nowrap">{isTamil ? 'அதிகபட்ச LTV*' : 'Maximum LTV*'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loanData.map((row, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.05 * index }}
                      className="hover:bg-slate-50 transition-colors duration-200 group"
                    >
                      <td className="py-5 px-6 text-slate-800 font-medium group-hover:text-primary-600 transition-colors duration-200">{row.type}</td>
                      <td className="py-5 px-6 text-slate-600">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold whitespace-nowrap">
                          {row.amount}
                        </span>
                      </td>
                      <td className="py-5 px-6 text-slate-600 font-medium whitespace-nowrap">{row.tenure}</td>
                      <td className="py-5 px-6">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-bold border border-blue-100 whitespace-nowrap">
                          {row.ltv}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 rounded-2xl p-6 border border-blue-100 flex flex-col sm:flex-row items-start gap-4 shadow-sm">
            <InformationCircleIcon className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed font-medium">
              *LTV means Loan-to-Value ratio. It is the percentage of the property’s eligible value that may be funded as a loan. Final LTV depends on the applicable product, property valuation, legal and technical assessment, and Aham HFC policy norms.
              {/* <a href="/Credit-Policy-10th-mar-2026.pdf" target="_blank" rel="noopener noreferrer" className="text-primary-600 font-bold ml-2 hover:underline inline-flex items-center gap-1 transition-colors duration-200 whitespace-nowrap mt-2 sm:mt-0">
                <DocumentTextIcon className="w-4 h-4" />
                Credit-Policy-10th-mar-2026.pdf
              </a> */}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 4. Static Disclaimer Section
// -------------------------------------------------------------
const StaticDisclaimerSection = () => {
  const { t, i18n } = useTranslation();
  const isTamil = i18n.language === 'ta';

  const disclaimerPoints = [
    isTamil ? 'உங்கள் வருமானம் மற்றும் வருமான மதிப்பீடு' : 'Your income and income assessment',
    isTamil ? 'தற்போதுள்ள EMIகள் மற்றும் பிற வழக்கமான நிதி கடமைகள்' : 'Existing EMIs and other regular financial obligations',
    isTamil ? 'திரும்பச் செலுத்தும் திறன்' : 'Repayment capacity',
    isTamil ? 'கடன் வரலாறு மற்றும் சிபில் (CIBIL) அறிக்கை' : 'Credit history and CIBIL report',
    isTamil ? 'சொத்து மதிப்பு, சட்ட மதிப்பாய்வு மற்றும் தொழில்நுட்ப மதிப்பீடு' : 'Property value, legal review and technical evaluation',
    isTamil ? 'தேவையான ஆவணங்கள் மற்றும் உள் கொள்கை விதிமுறைகள்' : 'Required documents and internal policy norms',
    isTamil ? 'பொருந்தக்கூடிய மற்றும் அனுமதிக்கப்பட்ட இடங்களில் இணை விண்ணப்பதாரரின் வருமானம்' : 'Co-applicant income, where applicable and permitted',
  ];

  return (
    <section className="py-24 bg-slate-950 relative overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-900/40 via-slate-950 to-slate-950"></div>
      <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-blue-900/20 to-transparent blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">

          {/* Header Block (Spans 12 on mobile, 5 on LG) */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:sticky lg:top-24"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 mb-8 shadow-[0_0_15px_rgba(239,68,68,0.15)]">
                <ShieldExclamationIcon className="w-5 h-5 text-red-400" />
                <span className="text-red-400 font-bold text-xs tracking-widest uppercase">
                  {isTamil ? 'முக்கியமான குறிப்பு' : 'Important Note'}
                </span>
              </div>

              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight leading-[1.15]">
                {isTamil ? 'இந்த கால்குலேட்டர் எதைச் சரிபார்க்காது' : 'What This Calculator Does Not Check'}
              </h2>

              <p className="text-lg md:text-xl text-slate-400 font-medium leading-relaxed mb-8">
                {isTamil
                  ? 'EMI கால்குலேட்டர் சாத்தியமான மாதாந்திர திருப்பிச் செலுத்துதலைத் திட்டமிட உதவுகிறது. இது கடன் ஒப்புதல் அல்லது இறுதி தகுதியை உறுதிப்படுத்தாது.'
                  : 'The EMI calculator helps you plan a possible monthly repayment. It does not confirm loan approval or final eligibility.'}
              </p>

              {/* Info Callout */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
                className="mt-4 bg-gradient-to-r from-primary-900/30 to-slate-800/40 border border-primary-800/40 rounded-2xl p-6 relative overflow-hidden group"
              >
                {/* Decorative sheen */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>

                <div className="relative z-10 flex gap-4 md:gap-5">
                  <div className="w-12 h-12 rounded-full bg-primary-900/50 border border-primary-500/30 flex items-center justify-center shrink-0 mt-1">
                    <InformationCircleIcon className="w-6 h-6 text-primary-400" />
                  </div>
                  <p className="text-slate-300 leading-relaxed font-medium text-sm md:text-base">
                    {isTamil
                      ? 'கொள்கை அனுமதிக்கும் இடங்களில், உண்மையான வருமான முறைகள் உட்பட உங்களின் ஒட்டுமொத்த நிலைமையை அஹம் HFC கவனமாகப் பார்க்கிறது. சுயதொழில் செய்பவர்கள், சிறு வணிக உரிமையாளர்கள் மற்றும் வழக்கமான சம்பள ஆவணங்கள் மூலம் வருமானம் எப்போதும் பிரதிபலிக்காத வாடிக்கையாளர்களுக்கு இது பயனுள்ளதாக இருக்கும்.'
                      : 'Aham HFC looks at your overall situation carefully, including real income patterns where policy permits. This can be useful for self-employed customers, small business owners and customers whose income may not always be reflected through standard salary documents.'}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Grid Block (Spans 12 on mobile, 7 on LG) */}
          <div className="lg:col-span-7">
            <div className="bg-slate-900/60 backdrop-blur-2xl border border-slate-800/80 rounded-[2rem] p-6 md:p-10 shadow-2xl">
              <h3 className="text-slate-400 font-bold uppercase tracking-widest text-xs mb-8 flex items-center gap-4">
                <span className="w-12 h-px bg-slate-700"></span>
                {isTamil ? 'உங்களின் உண்மையான கடன் தகுதி இவற்றைச் சார்ந்திருக்கலாம்:' : 'Your actual loan eligibility may depend on'}
                <span className="flex-1 h-px bg-slate-700"></span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {disclaimerPoints.map((point, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * index }}
                    className={`bg-slate-800/40 hover:bg-slate-800/90 border border-slate-700/50 hover:border-primary-500/50 rounded-2xl p-5 transition-all duration-300 group flex items-start gap-4 cursor-default ${index === disclaimerPoints.length - 1 ? 'sm:col-span-2' : ''}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-slate-900/80 border border-slate-700 flex items-center justify-center shrink-0 group-hover:bg-primary-900/50 group-hover:border-primary-500/50 transition-colors duration-300 shadow-inner">
                      <CheckCircleIcon className="w-5 h-5 text-slate-500 group-hover:text-primary-400 transition-colors duration-300" />
                    </div>
                    <span className="text-slate-300 font-medium group-hover:text-white transition-colors duration-300 mt-2 text-sm md:text-base">{point}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 5. Static How It Works Section
// -------------------------------------------------------------
const StaticHowItWorksSection = () => {
  const { t, i18n } = useTranslation();
  const isTamil = i18n.language === 'ta';

  const details = [
    {
      title: isTamil ? 'கடன் தொகை' : 'Loan amount',
      desc: isTamil ? 'நீங்கள் கடன் வாங்க திட்டமிட்டுள்ள தொகை' : 'The amount you plan to borrow',
      icon: CurrencyRupeeIcon,
      color: 'blue'
    },
    {
      title: isTamil ? 'வட்டி விகிதம்' : 'Interest rate',
      desc: isTamil ? 'திட்டமிடலுக்கு நீங்கள் பயன்படுத்த விரும்பும் விகிதம்' : 'The rate you want to use for planning',
      icon: ChartBarIcon,
      color: 'green'
    },
    {
      title: isTamil ? 'கடன் காலம்' : 'Loan tenure',
      desc: isTamil ? 'கடனை திருப்பிச் செலுத்த நீங்கள் தேர்ந்தெடுக்கும் ஆண்டுகளின் எண்ணிக்கை' : 'The number of years you choose to repay the loan',
      icon: ClockIcon,
      color: 'orange'
    }
  ];

  const getColorClasses = (color) => {
    switch (color) {
      case 'blue': return 'bg-blue-100 text-blue-600';
      case 'green': return 'bg-green-100 text-green-600';
      case 'orange': return 'bg-orange-100 text-orange-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-primary-50/50 via-white to-white pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center justify-center p-3 bg-primary-50 rounded-2xl mb-6 shadow-sm"
          >
            <CalculatorIcon className="w-8 h-8 text-primary-600" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-5xl font-extrabold text-slate-800 mb-6 tracking-tight"
          >
            {isTamil ? 'EMI கால்குலேட்டர் எப்படி வேலை செய்கிறது' : 'How the EMI Calculator Works'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-600 font-medium leading-relaxed max-w-2xl mx-auto"
          >
            {isTamil ? 'நீங்கள் மூன்று விவரங்களை மட்டுமே உள்ளிட வேண்டும்:' : 'You only need to enter three details:'}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          {details.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * index + 0.4 }}
              className="bg-white rounded-3xl p-8 border border-slate-100 shadow-xl hover:shadow-2xl transition-shadow duration-300 group"
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 ${getColorClasses(item.color)}`}>
                <item.icon className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">{item.title}</h3>
              <p className="text-slate-600 font-medium leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Info Cards below the grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7 }}
          className="max-w-4xl mx-auto space-y-6"
        >
          <div className="bg-primary-900 rounded-3xl p-8 text-center relative overflow-hidden shadow-2xl">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary-800/50 to-transparent"></div>
            <div className="relative z-10">
              <p className="text-white text-lg md:text-xl font-medium leading-relaxed">
                {isTamil
                  ? 'உங்கள் EMI ஐ மதிப்பிடுவதற்கு மாதாந்திர குறைப்பு-இருப்பு முறையை கால்குலேட்டர் பயன்படுத்துகிறது.'
                  : 'The calculator uses the monthly reducing-balance method to estimate your EMI.'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-blue-50/80 backdrop-blur-sm rounded-3xl p-8 border border-blue-100 shadow-sm flex flex-col gap-4">
              <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm text-blue-600 shrink-0">
                <InformationCircleIcon className="w-6 h-6" />
              </div>
              <p className="text-slate-700 font-medium leading-relaxed">
                {isTamil
                  ? 'நீண்ட கால அளவு மாதாந்திர EMI ஐக் குறைக்கலாம், ஆனால் இது முழு கடன் காலத்திற்கும் செலுத்தப்படும் மொத்த வட்டியை அதிகரிக்கலாம்.'
                  : 'A longer tenure can reduce the monthly EMI, but it may increase the total interest paid over the full loan period.'}
              </p>
            </div>

            <div className="bg-orange-50/80 backdrop-blur-sm rounded-3xl p-8 border border-orange-100 shadow-sm flex flex-col gap-4">
              <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm text-orange-600 shrink-0">
                <InformationCircleIcon className="w-6 h-6" />
              </div>
              <p className="text-slate-700 font-medium leading-relaxed">
                {isTamil
                  ? 'குறுகிய காலம் EMI ஐ அதிகரிக்கலாம் ஆனால் ஒட்டுமொத்த வட்டிச் செலவைக் குறைக்கலாம்.'
                  : 'A shorter tenure may increase the EMI but can reduce the overall interest outgo.'}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 6. Static What Changes EMI Section
// -------------------------------------------------------------
const StaticWhatChangesEMISection = () => {
  const { t, i18n } = useTranslation();
  const isTamil = i18n.language === 'ta';

  const factors = [
    {
      title: isTamil ? 'கடன் தொகை' : 'Loan amount',
      desc: isTamil
        ? 'கடன் வாங்கிய தொகை அதிகமாக இருந்தால், உங்கள் EMI அதிகமாக இருக்கலாம். அதிக முன்பணம் செலுத்தினால் நீங்கள் கடன் வாங்க வேண்டிய தொகையை குறைக்கலாம்.'
        : 'The higher the amount borrowed, the higher your EMI may be. A larger down payment can reduce the amount you need to borrow.',
      icon: CurrencyRupeeIcon,
      color: 'from-blue-400 to-indigo-500',
      shadow: 'shadow-blue-500/20'
    },
    {
      title: isTamil ? 'வட்டி விகிதம்' : 'Interest rate',
      desc: isTamil
        ? 'வட்டி விகிதத்தில் ஏற்படும் சிறிய மாற்றம் கூட உங்கள் EMI-யை பாதிக்கலாம். தயாரிப்பு, சுயவிவரம் மற்றும் கொள்கை விதிமுறைகளை பொறுத்து இறுதி விகிதம் அமையும்.'
        : 'Even a small change in the interest rate can affect your EMI, especially for larger loan amounts or longer tenures. Interest rates and final loan terms depend on the product, applicant profile, policy norms and final assessment.',
      icon: ChartBarIcon,
      color: 'from-emerald-400 to-teal-500',
      shadow: 'shadow-emerald-500/20'
    },
    {
      title: isTamil ? 'கடன் காலம்' : 'Loan tenure',
      desc: isTamil
        ? 'நீண்ட கால அளவு EMI-யை குறைக்கிறது, ஆனால் கடன் காலத்தின் போது செலுத்தப்படும் மொத்த வட்டியை அதிகரிக்கலாம்.'
        : 'A longer repayment tenure usually lowers the EMI by spreading payments across more months. However, it can increase the total interest paid during the loan period.',
      icon: ClockIcon,
      color: 'from-orange-400 to-red-500',
      shadow: 'shadow-orange-500/20'
    }
  ];

  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Abstract Background Shapes */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-600 rounded-full mix-blend-screen filter blur-[128px] opacity-20"></div>
        <div className="absolute top-40 -left-40 w-96 h-96 bg-indigo-600 rounded-full mix-blend-screen filter blur-[128px] opacity-20"></div>
        <div className="absolute -bottom-40 left-20 w-96 h-96 bg-blue-600 rounded-full mix-blend-screen filter blur-[128px] opacity-20"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight leading-tight"
          >
            {isTamil ? 'உங்கள் EMI-யை எது மாற்றும்?' : 'What Can Change Your EMI?'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg md:text-xl text-slate-300 font-medium leading-relaxed"
          >
            {isTamil
              ? 'உங்கள் மாதாந்திர EMI முக்கியமாக மூன்று விஷயங்களால் பாதிக்கப்படுகிறது:'
              : 'Your monthly EMI is mainly affected by three things:'}
          </motion.p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {factors.map((factor, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * index + 0.2 }}
              className="relative group h-full"
            >
              {/* Card background glowing effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${factor.color} rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>

              <div className={`relative h-full bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 p-8 rounded-3xl hover:border-slate-600/50 transition-colors duration-300 flex flex-col items-start ${factor.shadow} hover:shadow-2xl`}>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-br ${factor.color} text-white shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}>
                  <factor.icon className="w-7 h-7" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-4">{factor.title}</h3>

                <p className="text-slate-300 leading-relaxed font-medium text-[15px]">
                  {factor.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 7. Static Simple Example Section
// -------------------------------------------------------------
const StaticSimpleExampleSection = () => {
  const { t, i18n } = useTranslation();
  const isTamil = i18n.language === 'ta';

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#283079 2px, transparent 2px)', backgroundSize: '30px 30px' }}></div>
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-blue-50 to-transparent rounded-full blur-3xl opacity-70 -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-orange-50 to-transparent rounded-full blur-3xl opacity-70 translate-y-1/3 -translate-x-1/4 pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 border border-primary-100 mb-6"
          >
            <SparklesIcon className="w-5 h-5 text-primary-600" />
            <span className="text-primary-700 font-bold text-sm tracking-wide uppercase">
              {isTamil ? 'ஒரு எளிய உதாரணம்' : 'A Simple Example'}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl font-extrabold text-slate-800 mb-6 tracking-tight leading-tight"
          >
            {isTamil
              ? 'இரண்டு வாடிக்கையாளர்கள் ஒரே தொகையை ஒரே வட்டி விகிதத்தில் கடன் வாங்கினால்:'
              : 'If two customers borrow the same amount at the same interest rate:'}
          </motion.h2>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 relative">
          {/* Connector Line (visible on md+) */}
          <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent z-0"></div>
          <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full border border-slate-200 items-center justify-center text-slate-400 shadow-sm z-10 text-sm font-bold">
            VS
          </div>

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-[2rem] p-8 md:p-10 border border-slate-100 shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden group z-10"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700 pointer-events-none"></div>

            <div className="relative z-10 flex flex-col h-full">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mb-8 shadow-sm">
                <ClockIcon className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">
                {isTamil ? 'நீண்ட காலம்' : 'Longer Tenure'}
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed font-medium">
                {isTamil
                  ? 'நீண்ட கால அளவை தேர்ந்தெடுக்கும் வாடிக்கையாளருக்கு குறைந்த மாதாந்திர EMI இருக்கலாம்.'
                  : 'The customer who chooses a longer tenure may have a lower monthly EMI.'}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-[2rem] p-8 md:p-10 border border-slate-100 shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden group z-10"
          >
            <div className="absolute top-0 left-0 w-32 h-32 bg-orange-50 rounded-full blur-2xl -translate-y-1/2 -translate-x-1/2 group-hover:scale-150 transition-transform duration-700 pointer-events-none"></div>

            <div className="relative z-10 flex flex-col h-full">
              <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center mb-8 shadow-sm">
                <CurrencyRupeeIcon className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">
                {isTamil ? 'குறுகிய காலம்' : 'Shorter Tenure'}
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed font-medium">
                {isTamil
                  ? 'குறுகிய கால அளவை தேர்ந்தெடுக்கும் வாடிக்கையாளர் அதிக EMI-ஐச் செலுத்தலாம், ஆனால் கடனை விரைவாக முடித்து மொத்த வட்டியைக் குறைவாகச் செலுத்துவார்.'
                  : 'The customer who chooses a shorter tenure may pay a higher EMI but may finish repayment sooner and pay less total interest.'}
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-gradient-to-r from-primary-900 to-indigo-900 rounded-3xl p-8 md:p-10 text-center relative overflow-hidden shadow-2xl">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white/10 to-transparent pointer-events-none"></div>
            <div className="relative z-10 flex flex-col items-center">
              <CheckCircleIcon className="w-12 h-12 text-green-400 mb-4" />
              <p className="text-white text-xl md:text-2xl font-medium leading-relaxed max-w-3xl">
                {isTamil
                  ? 'உங்கள் குடும்ப பட்ஜெட் மற்றும் திருப்பிச் செலுத்தும் வசதிக்கு ஏற்றதே சரியான தேர்வாகும்.'
                  : 'The right choice is one that fits your household budget and repayment comfort.'}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 8. Static CTA Section
// -------------------------------------------------------------
const StaticCTASection = () => {
  const { t, i18n } = useTranslation();
  const isTamil = i18n.language === 'ta';

  const handleScrollToCalculator = (e) => {
    e.preventDefault();
    const element = document.getElementById('emi-calculator');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-24 bg-slate-50 relative overflow-hidden">
      {/* Decorative background vectors */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-100 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-100 rounded-full blur-3xl opacity-50 translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Main CTA Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-primary-900 via-primary-800 to-indigo-900 rounded-[2.5rem] p-8 md:p-14 shadow-2xl relative overflow-hidden mb-8"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400 opacity-10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4 pointer-events-none"></div>

            <div className="relative z-10 flex flex-col items-center text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mb-8 border border-white/20 shadow-inner"
              >
                <CalculatorIcon className="w-10 h-10 text-white" />
              </motion.div>

              <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight">
                {isTamil ? 'உங்கள் EMI-ஐ மதிப்பிட தயாரா?' : 'Ready to Estimate Your EMI?'}
              </h2>

              <p className="text-lg md:text-xl text-primary-100 mb-10 max-w-2xl leading-relaxed">
                {isTamil
                  ? 'மதிப்பிடப்பட்ட மாதாந்திர EMI-ஐக் காண, உங்களுக்கு விருப்பமான கடன் தொகை, கால அளவு மற்றும் வட்டி விகிதத்தை மேலே உள்ளீடு செய்யவும்.'
                  : 'Enter your preferred loan amount, tenure and interest rate above to see an estimated monthly EMI.'}
              </p>

              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="#emi-calculator"
                onClick={handleScrollToCalculator}
                className="bg-[#ed2636] hover:bg-red-700 text-white px-8 py-4 rounded-xl font-bold text-lg md:text-xl transition-all duration-300 shadow-[0_8px_30px_rgb(237,38,54,0.3)] hover:shadow-[0_8px_40px_rgb(237,38,54,0.5)] flex items-center gap-3 group cursor-pointer"
              >
                <span>{isTamil ? 'எனது EMI-ஐக் கணக்கிடு' : 'Calculate My EMI'}</span>
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
            </div>
          </motion.div>

          {/* Help / Disclaimer Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200 shadow-lg flex flex-col sm:flex-row items-start gap-5"
          >
            <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shrink-0 shadow-sm">
              <InformationCircleIcon className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-slate-800 mb-2">
                {isTamil ? 'உங்களுக்கு எவ்வளவு தகுதி உள்ளது என்பதைப் புரிந்து கொள்ள உதவி தேவையா?' : 'Need help understanding how much you may be eligible for?'}
              </h4>
              <p className="text-slate-600 font-medium leading-relaxed">
                {isTamil
                  ? 'உங்கள் வருமானம், தற்போதைய கடமைகள் மற்றும் வீட்டுக் கடன் தேவை குறித்து விவாதிக்க அஹம் HFC-யுடன் பேசுங்கள். இறுதி தகுதி மற்றும் விதிமுறைகள் மதிப்பீடு மற்றும் பொருந்தக்கூடிய கொள்கை விதிமுறைகளுக்கு உட்பட்டவை.'
                  : 'Speak with Aham HFC to discuss your income, existing commitments and home-loan requirement. Final eligibility and terms are subject to assessment and applicable policy norms.'}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 9. Static FAQ Section
// -------------------------------------------------------------
const StaticFAQSection = () => {
  const { t, i18n } = useTranslation();
  const isTamil = i18n.language === 'ta';

  const [openIndex, setOpenIndex] = useState(0);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  const faqs = [
    {
      q: isTamil ? 'வீட்டுக் கடன் EMI என்றால் என்ன?' : 'What is a home loan EMI?',
      a: isTamil
        ? 'வீட்டுக் கடன் EMI என்பது உங்கள் கடனுக்காக செலுத்தப்படும் நிலையான மாதாந்திர தவணையாகும். இதில் அசல் திருப்பிச் செலுத்துதல் மற்றும் வட்டி இரண்டும் அடங்கும்.'
        : 'A home loan EMI is the fixed monthly instalment paid towards your loan. It includes both principal repayment and interest.'
    },
    {
      q: isTamil ? 'எனது வீட்டுக் கடன் EMI எப்படி கணக்கிடப்படுகிறது?' : 'How is my home loan EMI calculated?',
      a: isTamil
        ? 'கடன் தொகை, வட்டி விகிதம் மற்றும் திருப்பிச் செலுத்தும் கால அளவு ஆகியவற்றைப் பயன்படுத்தி மாதாந்திர குறைப்பு-இருப்பு அடிப்படையில் உங்கள் EMI மதிப்பிடப்படுகிறது.'
        : 'Your EMI is estimated using the loan amount, interest rate and repayment tenure on a monthly reducing-balance basis.'
    },
    {
      q: isTamil ? 'நீண்ட கால அளவு எனது EMI-ஐக் குறைக்குமா?' : 'Does a longer tenure reduce my EMI?',
      a: isTamil
        ? 'ஆம். நீண்ட கால அளவு பொதுவாக மாதாந்திர EMI-ஐக் குறைக்கிறது, ஏனெனில் திருப்பிச் செலுத்துதல் அதிக மாதங்களுக்கு பரவுகிறது. இருப்பினும், கடன் காலத்தில் செலுத்தப்படும் மொத்த வட்டி அதிகமாக இருக்கலாம்.'
        : 'Yes. A longer tenure generally reduces the monthly EMI because repayment is spread over more months. However, the total interest paid over the loan period may be higher.'
    },
    {
      q: isTamil ? 'EMI கால்குலேட்டரைப் பயன்படுத்துவது எனது சிபில் (CIBIL) ஸ்கோரை பாதிக்குமா?' : 'Does using the EMI calculator affect my CIBIL score?',
      a: isTamil
        ? 'இல்லை. கால்குலேட்டரைப் பயன்படுத்துவது கடன் சரிபார்ப்பை உள்ளடக்காது மற்றும் உங்கள் சிபில் ஸ்கோரை பாதிக்காது.'
        : 'No. Using the calculator does not involve a credit check and does not affect your CIBIL score.'
    },
    {
      q: isTamil ? 'கால்குலேட்டர் காட்டும் EMI இறுதியானதா?' : 'Is the EMI shown by the calculator final?',
      a: isTamil
        ? 'இல்லை. நீங்கள் உள்ளிடும் மதிப்புகளை அடிப்படையாகக் கொண்ட ஒரு மதிப்பீடு மட்டுமே இது. இறுதி EMI, அங்கீகரிக்கப்பட்ட கடன் தொகை, வட்டி விகிதம், கால அளவு மற்றும் பிற விதிமுறைகளின் அடிப்படையில் கடன் மதிப்பீடு மற்றும் அனுமதிக்கப்பட்ட பின்னரே உறுதிப்படுத்தப்படும்.'
        : 'No. It is an estimate based on the values you enter. The final EMI will be confirmed only after loan assessment and sanction, based on the approved loan amount, interest rate, tenure and other applicable terms.'
    },
    {
      q: isTamil ? 'கால்குலேட்டர் எனது கடன் தகுதியைக் காட்டுகிறதா?' : 'Does the calculator show my loan eligibility?',
      a: isTamil
        ? 'இல்லை. கால்குலேட்டர் EMI-ஐ மட்டுமே மதிப்பிடுகிறது. கடன் தகுதி என்பது வருமானம், தற்போதைய கடமைகள், திருப்பிச் செலுத்தும் திறன், கடன் சுயவிவரம், சொத்து விவரங்கள், ஆவணங்கள் மற்றும் கொள்கை விதிமுறைகளைப் பொறுத்தது.'
        : 'No. The calculator estimates EMI only. Loan eligibility depends on factors such as income, existing obligations, repayment capacity, credit profile, property details, documentation and policy norms.'
    },
    {
      q: isTamil ? 'நான் திருப்பிச் செலுத்தும் அட்டவணையைப் பெற முடியுமா?' : 'Can I get a repayment schedule?',
      a: isTamil
        ? 'அனுமதிக்கப்பட்ட பிறகு, திருப்பிச் செலுத்தும் விதிமுறைகள் மற்றும் பொருந்தக்கூடிய கடன் அட்டவணை ஆகியவை அங்கீகரிக்கப்பட்ட இறுதி கடன் விதிமுறைகளின்படி வழங்கப்படும்.'
        : 'After sanction, the repayment terms and applicable loan schedule will be provided as per the final approved loan terms.'
    }
  ];

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl font-extrabold text-slate-800 mb-6 tracking-tight"
          >
            {isTamil ? 'அடிக்கடி கேட்கப்படும் கேள்விகள்' : 'Frequently Asked Questions'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-slate-600 font-medium"
          >
            {isTamil
              ? 'வீட்டுக் கடன் EMIகள் பற்றி நீங்கள் தெரிந்து கொள்ள வேண்டிய அனைத்தும்.'
              : 'Everything you need to know about home loan EMIs.'}
          </motion.p>
        </div>

        <div className="max-w-4xl mx-auto space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`border rounded-2xl overflow-hidden transition-all duration-300 ${isOpen ? 'bg-primary-50 border-primary-200 shadow-md' : 'bg-white border-slate-200 hover:border-primary-300 hover:shadow-sm'}`}
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none"
                >
                  <span className={`text-lg font-bold pr-4 transition-colors duration-300 ${isOpen ? 'text-primary-700' : 'text-slate-800'}`}>
                    {faq.q}
                  </span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${isOpen ? 'bg-primary-200 text-primary-700' : 'bg-slate-100 text-slate-500'}`}>
                    <ChevronDownIcon className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="px-6 pb-6 text-slate-600 font-medium leading-relaxed">
                    {faq.a}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 10. Main Page Component Wrapper
// -------------------------------------------------------------
const EMICalculatorFrontendPage = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is an EMI?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "EMI stands for equated monthly installment — the fixed amount you pay every month toward your home loan. Each installment covers part principal and part interest, in a ratio that shifts over the life of the loan."
        }
      },
      {
        "@type": "Question",
        "name": "How is my home loan EMI calculated?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "It depends on your loan amount, tenure, and interest rate, using the standard reducing-balance method. Enter your numbers into the calculator above and you'll see your estimate instantly."
        }
      },
      {
        "@type": "Question",
        "name": "Does a longer tenure reduce my EMI?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. A longer tenure lowers your monthly repayment because it's spread over more months. The trade-off is that you pay more interest in total by the time the loan is fully repaid."
        }
      },
      {
        "@type": "Question",
        "name": "Does using the calculator affect my CIBIL score?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. It's a free estimate tool with no credit check involved, so you can try as many combinations as you like without any impact on your credit report."
        }
      },
      {
        "@type": "Question",
        "name": "Can I get a full repayment schedule?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Once your loan is sanctioned, you'll get a year-wise amortization schedule showing exactly how much of each installment goes to principal and how much to interest."
        }
      },
      {
        "@type": "Question",
        "name": "Is this the same as checking my loan eligibility?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. This calculator estimates the monthly repayment for numbers you enter — it doesn't assess whether you'd be approved. Use the Home Loan Eligibility Scorecard for that."
        }
      }
    ]
  };

  return (
    <>
      <SEO
        title="Home Loan EMI Calculator | Estimate Your Monthly EMI | Aham HFC"
        description="Use Aham HFC’s free EMI calculator to estimate your monthly home loan EMI. Enter the loan amount, interest rate and tenure to plan your repayment with confidence."
        schema={faqSchema}
        canonical="https://ahamhfc.com/home-loan-emi-calculator"
      />
      <main className="overflow-hidden">
        <StaticHeroSection />
        <StaticEMICalculatorSection />
        <StaticHowItWorksSection />
        <StaticWhatChangesEMISection />
        <StaticSimpleExampleSection />
        <StaticLoanGuideSection />
        <StaticDisclaimerSection />
        <StaticCTASection />
        <StaticFAQSection />

      </main>
    </>
  );
};

export default EMICalculatorFrontendPage;
